import { DEFAULT_SETTINGS, STORAGE } from '../constants'
import { normalizeSettings } from './addons'
import type { MetaPreview, Settings, WatchProgress } from '../types'

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

/**
 * Progress is written from the player's timeupdate handler, so a quota or
 * private-mode failure here would throw straight out of playback. Every other
 * writer in the app already guards this; returns whether the value landed so a
 * caller can react rather than assume.
 */
function write<T>(key: string, value: T) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function boundedText(value: unknown, maximum: number) {
  return typeof value === 'string' ? value.slice(0, maximum) : undefined
}

function boundedHttpUrl(value: unknown, maximum: number) {
  if (typeof value !== 'string' || !value || value.length > maximum) return undefined
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:' ? value : undefined
  } catch { return undefined }
}

function cleanMeta(value: unknown): MetaPreview | null {
  if (!record(value) || typeof value.id !== 'string' || !value.id || typeof value.name !== 'string' || !value.name
    || (value.type !== 'movie' && value.type !== 'series')) return null
  const year = typeof value.year === 'number' && Number.isFinite(value.year)
    ? Math.max(0, Math.min(9999, value.year))
    : boundedText(value.year, 20)
  const list = (entry: unknown) => Array.isArray(entry)
    ? entry.filter((item): item is string => typeof item === 'string' && Boolean(item)).slice(0, 12).map((item) => item.slice(0, 40))
    : undefined
  return {
    id: value.id.slice(0, 240),
    type: value.type,
    name: value.name.slice(0, 200),
    imdb_id: boundedText(value.imdb_id, 240),
    poster: boundedHttpUrl(value.poster, 2_000),
    background: boundedHttpUrl(value.background, 2_000),
    logo: boundedHttpUrl(value.logo, 2_000),
    description: boundedText(value.description, 2_000),
    releaseInfo: boundedText(value.releaseInfo, 80),
    year,
    imdbRating: boundedText(value.imdbRating, 20),
    runtime: boundedText(value.runtime, 40),
    genre: list(value.genre),
    genres: list(value.genres),
    released: boundedText(value.released, 80),
    addonUrl: boundedHttpUrl(value.addonUrl, 500),
  }
}

function cleanProgress(value: unknown): WatchProgress | null {
  if (!record(value) || typeof value.key !== 'string' || !value.key || value.key.length > 300) return null
  const meta = cleanMeta(value.meta)
  if (!meta || typeof value.currentTime !== 'number' || !Number.isFinite(value.currentTime)
    || typeof value.duration !== 'number' || !Number.isFinite(value.duration)
    || typeof value.updatedAt !== 'number' || !Number.isFinite(value.updatedAt)) return null
  let video: WatchProgress['video']
  if (value.video !== undefined) {
    if (!record(value.video) || typeof value.video.id !== 'string' || !value.video.id) return null
    video = {
      id: value.video.id.slice(0, 240),
      title: boundedText(value.video.title, 200),
      name: boundedText(value.video.name, 200),
      season: Number.isInteger(value.video.season) ? Math.max(0, Math.min(100_000, Number(value.video.season))) : undefined,
      episode: Number.isInteger(value.video.episode) ? Math.max(0, Math.min(100_000, Number(value.video.episode))) : undefined,
      released: boundedText(value.video.released, 80),
      thumbnail: boundedHttpUrl(value.video.thumbnail, 2_000),
      overview: boundedText(value.video.overview, 2_000),
    }
  }
  return {
    key: value.key,
    meta,
    video,
    currentTime: Math.max(0, Math.min(86_400, value.currentTime)),
    duration: Math.max(0, Math.min(86_400, value.duration)),
    updatedAt: Math.max(0, Math.min(9_999_999_999_999, value.updatedAt)),
  }
}

export const getSettings = (): Settings => {
  const raw = read<unknown>(STORAGE.settings, {})
  const saved = record(raw) ? raw as Partial<Settings> : {}
  const hasSavedSettings = Object.keys(saved).length > 0
  const hasAddonList = Array.isArray(saved.addons)
  return normalizeSettings({
    ...DEFAULT_SETTINGS,
    ...saved,
    // A pre-manager install has legacy aliases but no list. A newer install
    // may intentionally persist an empty list, which must remain empty.
    addonsConfigured: hasSavedSettings ? (saved.addonsConfigured === true || hasAddonList) : true,
    addons: hasSavedSettings && !hasAddonList ? [] : (saved.addons || DEFAULT_SETTINGS.addons),
  })
}

export const saveSettings = (settings: Settings) => write(STORAGE.settings, normalizeSettings(settings))

export const getLibrary = () => {
  const value = read<unknown>(STORAGE.library, [])
  return Array.isArray(value) ? value.map(cleanMeta).filter((item): item is MetaPreview => Boolean(item)).slice(0, 100) : []
}

export function toggleLibrary(meta: MetaPreview) {
  const current = getLibrary()
  const clean = cleanMeta(meta)
  if (!clean) return current
  const exists = current.some((item) => item.id === clean.id && item.type === clean.type)
  const next = exists
    ? current.filter((item) => !(item.id === clean.id && item.type === clean.type))
    : [clean, ...current]
  return replaceLibrary(next)
}

export const getProgress = () => {
  const value = read<unknown>(STORAGE.progress, [])
  return (Array.isArray(value) ? value.map(cleanProgress).filter((item): item is WatchProgress => Boolean(item)) : [])
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 30)
}

export function saveProgress(item: WatchProgress) {
  const clean = cleanProgress(item)
  if (!clean) return getProgress()
  const current = getProgress().filter((entry) => entry.key !== clean.key)
  const finished = clean.duration > 0 && clean.currentTime / clean.duration > 0.94
  if (finished) markWatched(clean.key)
  const next = finished ? current : [clean, ...current].slice(0, 30)
  write(STORAGE.progress, next)
  return next
}

export function removeProgress(key: string) {
  const next = getProgress().filter((entry) => entry.key !== key)
  write(STORAGE.progress, next)
  return next
}

export function removeTitleProgress(type: string, id: string) {
  const prefix = `${type}:${id}:`
  const next = getProgress().filter((entry) => !entry.key.startsWith(prefix))
  write(STORAGE.progress, next)
  const watched = Object.fromEntries(Object.entries(getWatched()).filter(([key]) => !key.startsWith(prefix)))
  write(STORAGE.watched, watched)
  return next
}

export function clearWatchProgress() {
  write(STORAGE.progress, [])
  write(STORAGE.watched, {})
  return [] as WatchProgress[]
}

export function replaceLibrary(items: MetaPreview[]) {
  const seen = new Set<string>()
  const next = items.map(cleanMeta).filter((item): item is MetaPreview => {
    if (!item) return false
    const key = `${item.type}:${item.id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  }).slice(0, 100)
  write(STORAGE.library, next)
  return next
}

export function replaceProgress(items: WatchProgress[]) {
  const byKey = new Map<string, WatchProgress>()
  items.map(cleanProgress).forEach((item) => {
    if (!item) return
    const current = byKey.get(item.key)
    if (!current || item.updatedAt > current.updatedAt) byKey.set(item.key, item)
  })
  const next = Array.from(byKey.values()).sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 30)
  write(STORAGE.progress, next)
  return next
}

/* ------------------------------------------------------------------
   Finished items are dropped from progress, so completion is recorded
   separately — otherwise a watched episode looks unwatched forever.
   ------------------------------------------------------------------ */

export const getWatched = () => {
  const value = read<unknown>(STORAGE.watched, {})
  if (!record(value)) return {}
  return Object.fromEntries(Object.entries(value)
    .filter(([key, timestamp]) => key.length > 0 && key.length <= 300 && typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, 500)) as Record<string, number>
}

export function markWatched(key: string) {
  if (!key || key.length > 300) return getWatched()
  return replaceWatched({ ...getWatched(), [key]: Date.now() })
}

export function replaceWatched(value: Record<string, number>) {
  const entries = Object.entries(value)
    .filter(([key, timestamp]) => key.length <= 300 && Number.isFinite(timestamp) && timestamp > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 500)
  const next = Object.fromEntries(entries)
  write(STORAGE.watched, next)
  return next
}

/* ------------------------------------------------------------------
   What this connection has actually achieved. The player measures it;
   stream ranking uses it, so recommendations improve with use.
   ------------------------------------------------------------------ */

const SPEED_SAMPLES = 8

export function saveMeasuredSpeed(bytesPerSecond: number) {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return
  const saved = read<unknown>(STORAGE.network, [])
  const samples = (Array.isArray(saved) ? saved : []).filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0)
    .concat(bytesPerSecond).slice(-SPEED_SAMPLES)
  write(STORAGE.network, samples)
}

/** Median of recent samples, or undefined until there is real evidence. */
export function getMeasuredSpeed() {
  const saved = read<unknown>(STORAGE.network, [])
  const samples = (Array.isArray(saved) ? saved : [])
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0)
    .slice(-SPEED_SAMPLES).sort((a, b) => a - b)
  if (samples.length < 2) return undefined
  const middle = Math.floor(samples.length / 2)
  return samples.length % 2 ? samples[middle] : (samples[middle - 1] + samples[middle]) / 2
}

/* ------------------------------------------------------------------
   Vela cannot play anything without a streaming server, so a fresh
   install is walked through pointing at one before it browses.
   ------------------------------------------------------------------ */

export const isSetupComplete = () => read<unknown>(STORAGE.setup, false) === true
export const completeSetup = () => write(STORAGE.setup, true)

export const getRecentSearches = () => {
  const value = read<unknown>(STORAGE.searches, [])
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim().length >= 2).map((item) => item.slice(0, 120)).slice(0, 6)
    : []
}

export function saveRecentSearch(query: string) {
  const value = query.trim().slice(0, 120)
  if (value.length < 2) return getRecentSearches()
  const next = [value, ...getRecentSearches().filter((item) => item.toLowerCase() !== value.toLowerCase())].slice(0, 6)
  write(STORAGE.searches, next)
  return next
}

/** Drop disposable measurements and browser caches without touching a
 * viewer's library, settings, progress, or watched history. */
export async function clearAppCache() {
  localStorage.removeItem(STORAGE.network)
  Object.keys(localStorage).filter((key) => key.startsWith(STORAGE.compatibility)).forEach((key) => localStorage.removeItem(key))
  if (typeof caches !== 'undefined') {
    const keys = await caches.keys()
    await Promise.all(keys.map((key) => caches.delete(key)))
  }
}
